/**
 * The temperature control for one burner — a thermometer standing beside the
 * apparatus it belongs to. Temperature isn't a property of the lab, it's a
 * property of a particular burner, so the control lives with the burner rather
 * than in a side panel that could only ever address one container at a time.
 *
 * Vertical, and shaped like the instrument a student would actually reach for:
 * a bulb at the bench and a column that fills as it gets hotter. Dragging the
 * column sets the burner. The scale is continuous 0-200 °C because that is how
 * a burner behaves, and it says nothing about whether the contents are boiling
 * — that depends on the liquid's own boiling point (temperature.ts).
 *
 * Drawn as native SVG rather than an <input type="range"> in a foreignObject:
 * a range input nested in scaled SVG does not reliably receive drag events
 * (Safari in particular), which left the control looking usable and refusing
 * to move. Pointer capture on a plain rect works everywhere.
 */
import { useRef, useState } from 'react';
import { TEMP_MIN_C, TEMP_MAX_C, TEMP_STEP_C, formatTemp } from '../temperature';

/** Bench to the top of the column. Short on purpose — it stands beside the
 *  glassware rather than towering over it, and 5 °C still lands on its own
 *  couple of pixels. */
export const BENCH_TEMP_HEIGHT = 78;
/** How far out from the vessel the thermometer stands. */
export const BENCH_TEMP_OFFSET = 26;
/** How far its bulb is held clear of the bench — a thermometer reading a
 *  vessel is held in it or clamped beside it, never resting on the desk. */
export const BENCH_TEMP_LIFT = 26;

const STEM_W = 10;
const BULB_R = 8;
const STEM_TOP = -BENCH_TEMP_HEIGHT;
const STEM_BOTTOM = -BULB_R - 1;
const STEM_SPAN = STEM_BOTTOM - STEM_TOP;
/** Grab area, wider than the stem so it is easy to catch. */
const HIT_W = 26;

function snap(raw: number): number {
  const clamped = Math.min(TEMP_MAX_C, Math.max(TEMP_MIN_C, raw));
  return Math.round(clamped / TEMP_STEP_C) * TEMP_STEP_C;
}

export function BenchTemperature({ celsius, disabled, gradientId, onSet }: {
  celsius: number;
  disabled: boolean;
  /** Unique per container — SVG gradient ids are document-global. */
  gradientId: string;
  onSet: (celsius: number) => void;
}): React.JSX.Element {
  const stemRef = useRef<SVGRectElement>(null);
  const [dragging, setDragging] = useState(false);

  const fraction = (celsius - TEMP_MIN_C) / (TEMP_MAX_C - TEMP_MIN_C);
  const levelY = STEM_BOTTOM - fraction * STEM_SPAN;

  /** Screen y → °C. Measured off the rendered stem, so it stays correct
   *  however the scene's viewBox happens to be scaled. Inverted: the bench end
   *  of a thermometer is the cold end. */
  function valueAt(clientY: number): number {
    const rect = stemRef.current?.getBoundingClientRect();
    if (!rect || rect.height === 0) return celsius;
    const fromBottom = (rect.bottom - clientY) / rect.height;
    return snap(TEMP_MIN_C + fromBottom * (TEMP_MAX_C - TEMP_MIN_C));
  }

  function nudge(delta: number): void {
    const next = snap(celsius + delta);
    if (next !== celsius) onSet(next);
  }

  return (
    <g
      role="slider"
      aria-label="Burner temperature in degrees Celsius"
      aria-valuemin={TEMP_MIN_C}
      aria-valuemax={TEMP_MAX_C}
      aria-valuenow={celsius}
      aria-valuetext={formatTemp(celsius)}
      aria-orientation="vertical"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') { e.preventDefault(); nudge(-TEMP_STEP_C); }
        if (e.key === 'ArrowUp' || e.key === 'ArrowRight') { e.preventDefault(); nudge(TEMP_STEP_C); }
      }}
      style={{ opacity: disabled ? 0.45 : 1 }}
    >
      <defs>
        {/* Cool at the bench end, hot at the top — the column carries the scale
            itself, so it reads as a temperature and not a generic control. */}
        <linearGradient id={gradientId} x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#4f9de0" />
          <stop offset="55%" stopColor="#ffb020" />
          <stop offset="100%" stopColor="#ff5a3d" />
        </linearGradient>
        <clipPath id={`${gradientId}-fill`}>
          <rect x={-STEM_W} y={levelY} width={STEM_W * 2} height={STEM_BOTTOM - levelY + BULB_R * 2} />
        </clipPath>
      </defs>

      <text x={0} y={STEM_TOP - 8} textAnchor="middle" fontSize={11} fontWeight={600} className="chemlab-therm-ink">
        {formatTemp(celsius)}
      </text>

      {/* Glass: the empty column and its bulb. */}
      <rect
        ref={stemRef} x={-STEM_W / 2} y={STEM_TOP} width={STEM_W} height={STEM_SPAN} rx={STEM_W / 2}
        className="chemlab-therm-glass" strokeWidth={1.6}
      />
      <circle cx={0} cy={0} r={BULB_R} className="chemlab-therm-glass" strokeWidth={1.6} />

      {/* The reading: bulb and column filled up to the set temperature. */}
      <g clipPath={`url(#${gradientId}-fill)`}>
        <rect
          x={-STEM_W / 2 + 2} y={STEM_TOP} width={STEM_W - 4} height={STEM_SPAN + BULB_R}
          rx={(STEM_W - 4) / 2} fill={`url(#${gradientId})`}
        />
      </g>
      <circle cx={0} cy={0} r={BULB_R - 2.5} fill="#ff5a3d" />

      {/* Graduations every 50 °C. */}
      <g className="chemlab-therm-edge" strokeWidth={1.4} strokeLinecap="round">
        {[0.5].map(t => (
          <line key={t} x1={STEM_W / 2 + 1} y1={STEM_BOTTOM - t * STEM_SPAN} x2={STEM_W / 2 + 6} y2={STEM_BOTTOM - t * STEM_SPAN} />
        ))}
      </g>

      <rect
        x={-HIT_W / 2} y={STEM_TOP - 6} width={HIT_W} height={STEM_SPAN + BULB_R + 12}
        fill="transparent"
        style={{ cursor: disabled ? 'default' : 'ns-resize' }}
        // The bench beneath listens for right-clicks (the stand menu) and the
        // vessels for clicks; dragging the burner's scale is neither.
        onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onPointerDown={(e) => {
          if (disabled) return;
          e.stopPropagation();
          e.currentTarget.setPointerCapture(e.pointerId);
          setDragging(true);
          onSet(valueAt(e.clientY));
        }}
        onPointerMove={(e) => { if (dragging) onSet(valueAt(e.clientY)); }}
        onPointerUp={(e) => {
          e.currentTarget.releasePointerCapture(e.pointerId);
          setDragging(false);
        }}
        onPointerCancel={() => setDragging(false)}
      />
    </g>
  );
}
